import { useState, useEffect, useCallback, useRef } from "react";
import { useStore } from "react-redux";
import i18n from "i18next";
import { LuPlay, LuPause } from "react-icons/lu";
import AppModal from "../common/modal/AppModal";
import Lightbox from "./Lightbox";
import { fetchBulkAnimatePreflight, approveJob } from "../../../common/server_utils";
import { mapAiProviderErrorMessage } from "../../../common/ai_provider_errors.es6";
import { useBulkAnimateBatch } from "../../hooks/useBulkAnimateBatch";
import { revertAnimatedBox } from "../../utils/revertAnimatedBox.es6";
import { applyBulkAnimationResults } from "../../utils/applyBulkAnimationResults.es6";
import {
  buildLiveByBoxId,
  deriveCardLiveState,
  isVideoUrl,
  isResultApplied,
  findAppliedAnimation,
  deriveCardAnimateAction,
} from "../../utils/animatorCardStatus.es6";
import { computeScenePlayhead, pickActiveCardBoxId } from "../../utils/scenePreviewSync.es6";
import { getUndoSignature, hasUndoSignatureChanged } from "../../utils/autosaveDecision.es6";
import { deriveProviderOptions } from "../../utils/animatorProviderOptions.es6";
import { pickAnimationDuration } from "../../utils/pickAnimationDuration.es6";
import { supportedDurationsForProvider } from "../../utils/animationDurationOptions.es6";
import SceneAudioService from "../../services/sceneAudioService";
import "./_animatorPanel.scss";

// AnimatorPanel — per-scene view of a training's images and their animated
// status, grouped by scene. Step 2 of the animation-panel redesign added
// "Animate all in this scene" per-scene bulk-animate. Step 3 added a second
// image source: a header toggle switches between "Infographic" and "All
// images". Phase 3 (generated-image provenance) moved BOTH options onto the
// server preflight endpoint (source=infographic|all) instead of deriving
// "All images" from Redux, so the inventory reflects server-side
// exportedState rather than the live canvas. Opening the panel fetches
// preflight immediately and does NOT save first — auto-saving on open was a
// data-loss vector (a stale tab could silently overwrite newer server state
// with older canvas state). A notice tells the user the list is the
// last-saved version; "Save and refresh" (handleSaveAndRefresh) is the only
// thing that writes, and only in direct response to a click, reusing the
// same save path before re-fetching. Batch lifecycle (start/poll/cancel/
// apply) goes through useBulkAnimateBatch regardless of image source.
//
// Card states (product-owner redesign): each image renders as one of THREE
// states, not two -- alreadyAnimated (an animation exists in the library) is
// NOT the same as "applied to the box" (the canvas actually shows it).
// Neither preflight nor /active tracks "applied", so isResultApplied
// (animatorCardStatus.es6) reads live Redux pluginToolbarsById as the one
// narrow exception to the API-only data source described above, and only to
// answer that one question -- see its own comment for exactly what it checks
// and its documented blind spot (a box swapped before the originalImageUrl
// stash existed can't be detected and falls back to "not applied", never the
// reverse). A card's own "Onayla"/Approve applies just that one result
// (applyBulkAnimationResults with a single-item array, synchronous, excluded
// from undo like every other apply path) without touching the batch/apply
// lifecycle above. The header's refresh affordance is conditional: a
// compact "Save and refresh" swaps in only when hasUndoSignatureChanged says
// there's a real unsaved edit (getLastSavedSignature prop mirrors the nav
// guard's own baseline read in EditorApp.jsx); otherwise a plain "Refresh
// list" button just re-fetches preflight, no save step (Task 46 folded both
// into the header row -- see the toolbar-left JSX).
//
// Task 37: a provider/model dropdown (header, resets to the server's
// defaults.provider each open, see load()'s resetProvider option) feeds both
// bulk actions. Each card's own Animate/Re-animate button no longer starts a
// bulk-of-one -- it closes this panel and opens the normal single-image
// ImageAnimatorModal popup (via the `aidea:openImageAnimator` window event,
// handled by EditorShortcuts.jsx), which isn't subject to the bulk batch's
// one-per-training lock, so it stays available during a running batch except
// for a card whose own item is running in that batch (see
// deriveCardAnimateAction). The event's `returnTo` marker lets the popup's
// Back button reopen this panel on the same scene, refreshing preflight.

const getTrainingId = () =>
  window.idealStudioTrainingId || new URLSearchParams(window.location.search).get("id");

// Stable empty set for scenes with no selection entry yet (avoids a new Set()
// per render breaking referential checks).
const EMPTY_SET = new Set();

// Selectable = the animate-apply flow can write a result back into it (has a
// toolbar to update) and isn't already animated. Both are server-derived —
// preflight's `animatable` flag replaces the old client-side raster-format
// check — so the rendering never has to branch on where an image came from.
const isSelectableForAnimate = (img) => !img.alreadyAnimated && !!img.animatable;

// Time-range label per card (Task B) -- preflight gives seconds directly per
// image. null start/end means "no explicit timing" and must never be shown
// as a fabricated number; the honest fallback is "whole scene".
const formatTimeRange = (timing) => {
  const start = timing?.startSec;
  const end = timing?.endSec;
  if (start == null || end == null) {
    return i18n.t("AnimatorPanel.WholeScene", { defaultValue: "whole scene" });
  }
  return i18n.t("AnimatorPanel.TimeRange", {
    start: Math.round(start),
    end: Math.round(end),
    defaultValue: "{{start}}–{{end}} sec",
  });
};

// null start/end (no explicit timing, i.e. "whole scene") has no screen time
// to size a duration from.
const screenTimeSecFromTiming = (timing) => {
  const start = timing?.startSec;
  const end = timing?.endSec;
  return start == null || end == null ? null : end - start;
};

// Per-item durationSec (and, when needed, provider) for a bulk-animate start
// payload (Task 42, escalation added Task 46). A null screen time sends
// neither field so the batch-wide/server default applies instead -- matches
// the DTO's per-item > batch-wide > default precedence.
//
// Escalation: screen time > 15s overrides that item's provider to pixverse@
// 15s (the longest clip any provider supports) instead of clamping to the
// selected provider's own max (8s for gemini/gemini-fast). Guarded by
// preflight's own `providers` list (env-gated, same list the header dropdown
// reads) -- an item-level provider the server doesn't have configured 400s
// the whole batch (animate-v2-bulk.service.ts isProviderAvailable), so an
// unavailable pixverse falls back to the normal closest-duration pick below
// instead of sending an override.
const PIXVERSE_ESCALATION_SEC = 15;
const itemAnimateOverrides = (timing, supportedDurations, availableProviders) => {
  const screenTimeSec = screenTimeSecFromTiming(timing);
  if (screenTimeSec != null && screenTimeSec > PIXVERSE_ESCALATION_SEC) {
    if ((availableProviders || []).includes("pixverse")) {
      return { provider: "pixverse", durationSec: PIXVERSE_ESCALATION_SEC };
    }
  }
  const durationSec = pickAnimationDuration(screenTimeSec, supportedDurations);
  return durationSec !== undefined ? { durationSec } : {};
};

export default function AnimatorPanel({ dispatch, onSaved, onConflict, getLastSavedSignature }) {
  const store = useStore();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [preflight, setPreflight] = useState(null);
  const [selectedNavId, setSelectedNavId] = useState(null);
  const [lightboxUrl, setLightboxUrl] = useState(null);
  const [confirmScope, setConfirmScope] = useState(null); // null | "scene" | "all"
  const [source] = useState("all"); // forced to "all images" — kaynak filtresi kaldırıldı
  // Provider/model dropdown (Task 37 ASK 1) -- session-only, reset to the
  // server's defaults.provider each time the panel opens (see load()'s
  // resetProvider option below), never persisted across sessions.
  const [selectedProvider, setSelectedProvider] = useState(null);
  // Auto/Custom seçimi (Avatar sekmesindeki AI Provider toggle'ı gibi). "auto"
  // → selectedProvider null bırakılır, sunucu defaults.provider'ı kullanılır;
  // "custom" → kullanıcı chip'lerden birini seçer. Her açılışta "auto"ya döner.
  const [providerMode, setProviderMode] = useState("auto");
  // Custom-mode ayarları (auto'da yok sayılır). "auto" → sunucu defaults'ı;
  // "custom" → kullanıcı ImageAnimatorModal'daki gibi süre/döngü/çıktı seçer.
  // durationSec null = "Auto" (sağlayıcı kendi varsayılanını uygular). Her
  // açılışta (load resetProvider) sunucu defaults'ına döner.
  const [selectedDuration, setSelectedDuration] = useState(null); // null = Auto
  const [selectedLoop, setSelectedLoop] = useState(true);
  const [selectedOutput, setSelectedOutput] = useState("webp");
  // Bulk-animate görsel seçimi. Backend (startBulkAnimate) items[] ile keyfi
  // subset kabul ediyor, o yüzden seçim tamamen istemci tarafında: sahnedeki
  // animate edilebilir görsellerin HEPSİ seçili başlar, kullanıcı istemediğini
  // kaldırır, yalnızca seçili olanlar animate edilir. Seçim navId başına
  // saklanır (Map): sahneler arasında gezerken ÖNCEKİ sahnenin seçimi korunur,
  // sıfırlanmaz. Preflight yenilenince tüm seçimler sıfırlanır.
  const [selectionByNav, setSelectionByNav] = useState({});
  // Per-card revert (turn an animated box back into its original still image)
  // — kept separate from confirmScope so it never stacks with the panel-wide
  // animate confirm strips, and scoped by boxId so only the clicked card's
  // action row swaps to the confirm/spinner state.
  const [revertConfirmBoxId, setRevertConfirmBoxId] = useState(null);
  const [revertingBoxId, setRevertingBoxId] = useState(null);
  const [revertError, setRevertError] = useState(null); // { boxId, message }
  // Narration preview (Task A/B/C) -- backend-driven per the product-owner
  // constraint, joined to preflight scenes by navId (never derived from
  // Redux). sceneAudios is the raw GET scene-audios response; playheadSec is
  // null while stopped/never-played so no card renders as highlighted (see
  // stopPreview below and pickActiveCardBoxId in scenePreviewSync.es6).
  const [sceneAudios, setSceneAudios] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [playheadSec, setPlayheadSec] = useState(null);
  const audioRef = useRef(null);

  // opts.targetNavId (Task 37 ASK 4): select this scene once preflight loads,
  // when it's still present in the fresh data -- used by handleOpen when the
  // panel is reopened from the single-image popup's Back button, so the user
  // lands back on the scene they came from instead of the first one.
  // opts.resetProvider (ASK 1): reset the provider dropdown to the server's
  // defaults.provider -- only handleOpen passes this, so a mid-session
  // refresh/source-toggle never reverts the user's in-session pick.
  const load = useCallback(
    (sourceOverride, opts) => {
      const trainingId = getTrainingId();
      if (!trainingId) {
        setError(true);
        return;
      }
      setLoading(true);
      setError(false);
      fetchBulkAnimatePreflight(trainingId, sourceOverride || source).then((data) => {
        setLoading(false);
        if (!data) {
          setError(true);
          return;
        }
        setPreflight(data);
        const targetNavId = opts?.targetNavId;
        setSelectedNavId((prev) => {
          if (targetNavId && data.scenes?.some((s) => s.navId === targetNavId)) return targetNavId;
          return data.scenes?.some((s) => s.navId === prev)
            ? prev
            : data.scenes?.[0]?.navId || null;
        });
        if (opts?.resetProvider) {
          // Auto: sunucu defaults.provider'ı kullanılır (selectedProvider null).
          setProviderMode("auto");
          setSelectedProvider(null);
          // Custom kontrolleri de sunucu defaults'ına döndür.
          setSelectedDuration(null);
          setSelectedLoop(typeof data.defaults?.loop === "boolean" ? data.defaults.loop : true);
          setSelectedOutput(data.defaults?.outputs?.[0] || "webp");
        }
      });
    },
    [source]
  );

  const {
    items: liveItems,
    anyPending,
    starting,
    applyingNavId,
    error: bulkError,
    checkActive,
    start,
    cancelScene,
    applyScene,
  } = useBulkAnimateBatch({ dispatch, onApplied: load });

  // Stops narration playback and clears the highlight (playheadSec -> null).
  // Called on scene switch, panel close, and starting a bulk animation (see
  // call sites below) so a stale card never stays highlighted.
  const stopPreview = () => {
    if (audioRef.current) {
      audioRef.current.pause();
    }
    setPlaying(false);
    setPlayheadSec(null);
  };

  // Fetches immediately on open — never saves as a side effect of opening
  // (see file header). "Save and refresh" below is the only path that calls
  // window.ideaEnvSaveState, and only in direct response to a click.
  const handleOpen = useCallback(
    (e) => {
      setOpen(true);
      setConfirmScope(null);
      setPreflight(null);
      setError(false);
      setSaveError(false);
      stopPreview();
      // Tüm approved-olmayan batch'leri benimse (çalışan + apply bekleyen).
      // Faz artık per-scene olduğundan terminal bir sahne diğerlerini
      // kilitlemez; kullanıcı yeniden açınca apply bekleyen sahneyi görür.
      checkActive();
      // Default to the scene the user is CURRENTLY on in the editor
      // (navItemSelected). The panel never unmounts, so selectedNavId otherwise
      // persists across opens and reopening lands on a stale, last-viewed scene
      // instead of where the user actually is. e.detail.navId still wins when the
      // single-image popup's Back button reopens the panel (ASK 4).
      const currentNavId = store.getState()?.undoGroup?.present?.navItemSelected || null;
      load(undefined, {
        resetProvider: true,
        targetNavId: e?.detail?.navId || currentNavId,
      });
      const trainingId = getTrainingId();
      if (trainingId) {
        // Narration text/audio for the preview area -- reuses the existing
        // scene-audios client (see AIVoiceTab/ImageAnimatorModal), best-effort:
        // a failure here only empties the narration preview, it must not block
        // the rest of the panel.
        SceneAudioService.getSceneAudios({ trainingId })
          .then((data) => setSceneAudios(data))
          .catch(() => setSceneAudios(null));
      } else {
        setSceneAudios(null);
      }
    },
    [load, checkActive, store]
  );

  const handleClose = () => {
    stopPreview();
    setOpen(false);
  };

  // Explicit, user-initiated save: reuses the same save function the
  // toolbar's Save button and the parent-shell "triggerSave" postMessage
  // contract both call (window.ideaEnvSaveState, exposed in index.jsx)
  // rather than inventing a new save path, then re-fetches preflight so the
  // list reflects what was just saved. On failure, surfaces saveError next
  // to the button and leaves the currently-shown (last-saved) list alone —
  // never presents it as fresher than it actually is.
  const handleSaveAndRefresh = useCallback(() => {
    const trainingId = getTrainingId();
    if (!trainingId) {
      setError(true);
      return;
    }
    setSaving(true);
    setSaveError(false);
    // Ambient flag (same technique as window.bulkAnimateApplying, reducers/
    // reducers.jsx:413) so the opt-in autosave timer in EditorApp.jsx can
    // skip a tick while this write is in flight.
    window.animatorPanelSaving = true;
    window.ideaEnvSaveState(
      { ...store.getState() },
      false,
      () => {
        window.animatorPanelSaving = false;
        setSaving(false);
        // Report back to EditorApp so the unsaved-changes guard's baseline
        // (and the opt-in autosave timer) don't keep treating this write as
        // pending -- the other four save paths do the same, see EditorApp.jsx.
        if (onSaved) onSaved();
        stopPreview();
        load();
      },
      (err) => {
        window.animatorPanelSaving = false;
        setSaving(false);
        // A conflict means another session (or the SCENE_LOGGING re-save)
        // saved a newer version -- route to the same SaveConflictModal every
        // other save path uses instead of the generic error banner, closing
        // first so it doesn't stack under that modal (no popup over a popup).
        if (err?.isSaveConflict) {
          stopPreview();
          setOpen(false);
          if (onConflict) onConflict(err.currentUpdatedAt);
          return;
        }
        setSaveError(true);
      }
    );
  }, [store, load, onSaved, onConflict]);

  useEffect(() => {
    window.addEventListener("aidea:openAnimatorPanel", handleOpen);
    return () => window.removeEventListener("aidea:openAnimatorPanel", handleOpen);
  }, [handleOpen]);

  // Dev-only guard: an /active item whose boxId can't be matched to any
  // preflight image renders as a plain, unprogressed card -- the exact
  // "looks frozen" symptom a key/vocabulary drift between the two endpoints
  // would produce. Surface it loudly instead of letting it fail silently.
  useEffect(() => {
    if (process.env.NODE_ENV === "production" || !liveItems?.length) return;
    const knownBoxIds = new Set(
      (preflight?.scenes || []).flatMap((s) => (s.images || []).map((img) => img.boxId))
    );
    liveItems
      .filter((it) => !knownBoxIds.has(it.boxId))
      .forEach((it) => {
        console.warn(
          `AnimatorPanel: bulk-animate item boxId="${it.boxId}" (jobId=${it.jobId}) has no matching preflight image boxId -- its progress can't render on any card.`,
          { itemBoxId: it.boxId, knownBoxIds: Array.from(knownBoxIds) }
        );
      });
  }, [liveItems, preflight]);

  const selectScene = (navId) => {
    setSelectedNavId(navId);
    setConfirmScope(null);
    setRevertConfirmBoxId(null);
    setRevertError(null);
    stopPreview();
  };

  const scenes = preflight?.scenes || [];
  const selectedScene = scenes.find((s) => s.navId === selectedNavId) || scenes[0] || null;
  const hasImages = scenes.some((s) => (s.images || []).length > 0);
  // Aktif sahnenin seçili boxId kümesi (Map'ten). Girişi yoksa boş.
  const activeNavId = selectedScene?.navId || null;
  const selectedBoxIds = selectionByNav[activeNavId] || EMPTY_SET;

  const selectedImages = selectedScene?.images || [];
  // Grid'de gösterilecekler: yalnızca gerçek görseller (animate edilebilir veya
  // zaten animate edilmiş). Desteklenmeyen/image olmayan asset'ler gizli.
  const displayableImages = selectedImages.filter((img) => img.alreadyAnimated || img.animatable);
  const notAnimated = selectedImages.filter(isSelectableForAnimate);
  // Kullanıcının seçili bıraktığı (animate edilecek) görseller.
  const selectedNotAnimated = notAnimated.filter((img) => selectedBoxIds.has(img.boxId));
  const liveByBoxId = buildLiveByBoxId(liveItems);
  // Aktif sahnenin canlı item'ları (tüm batch'lerden navId ile süzülür).
  const sceneLiveItems = liveItems.filter((it) => it.navId === activeNavId);
  const sceneDone = sceneLiveItems.filter((it) => {
    const { isDone, isFailed } = deriveCardLiveState(it);
    return isDone || isFailed;
  }).length;
  // Aktif sahnenin fazı — global değil, per-scene. Hiç item yok → idle
  // (canlandır butonu); en az biri çalışıyor → running (ilerleme+iptal);
  // hepsi terminal → terminal (bu sahneye uygula). Başka sahnede iş çalışsa
  // bile bu sahne idle ise seçim/başlatma açık kalır.
  const scenePhase = (() => {
    if (!sceneLiveItems.length) return "idle";
    const allTerminal = sceneLiveItems.every((it) => {
      const { isDone, isFailed } = deriveCardLiveState(it);
      return isDone || isFailed;
    });
    return allTerminal ? "terminal" : "running";
  })();
  // Hangi sahnelerde hâlâ çalışan (terminal olmayan) job item'ı var — rail'de
  // o sahnelere spinner koymak için. Item'lar navId taşıyor, o yüzden başka
  // sahneye geçince bile çalışan sahne belli olur.
  const runningNavIds = new Set(
    liveItems
      .filter((it) => {
        const { isDone, isFailed } = deriveCardLiveState(it);
        return !isDone && !isFailed;
      })
      .map((it) => it.navId)
  );

  // Preflight yenilenince tüm sahnelerin seçimini sıfırla (yeni envanter).
  useEffect(() => {
    setSelectionByNav({});
  }, [preflight]);

  // Aktif sahnenin henüz seçim girişi yoksa "hepsi seçili" ile başlat. Girişi
  // olan sahneye dönünce ÖNCEKİ seçim korunur (sıfırlanmaz) — sahneler arası
  // gezinme seçimi bozmaz.
  useEffect(() => {
    if (!activeNavId) return;
    setSelectionByNav((prev) => {
      if (prev[activeNavId]) return prev;
      const ids = (selectedScene?.images || [])
        .filter(isSelectableForAnimate)
        .map((img) => img.boxId);
      return { ...prev, [activeNavId]: new Set(ids) };
    });
    // selectedScene aktif sahneden türetiliyor; activeNavId + preflight yeterli.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeNavId, preflight]);

  const toggleSelect = (boxId) =>
    setSelectionByNav((prev) => {
      const next = new Set(prev[activeNavId] || []);
      if (next.has(boxId)) next.delete(boxId);
      else next.add(boxId);
      return { ...prev, [activeNavId]: next };
    });

  const providerOptions = deriveProviderOptions(
    preflight?.providers,
    preflight?.defaults?.provider
  );
  // Supported durations for whichever provider a bulk-animate would actually
  // use right now -- feeds the "predicted duration" label on not-yet-animated
  // cards below (see itemDurationSec, same pure function the bulk start
  // payload uses).
  const activeSupportedDurations = supportedDurationsForProvider(
    selectedProvider || preflight?.defaults?.provider
  );

  // Read once per render: source of pluginToolbarsById for isResultApplied
  // (see file header -- the one place this panel reads Redux instead of the
  // API) and of the current undo signature for the unsaved-changes banner
  // below. store.getState() is a cheap reference read, not a clone.
  const reduxState = store.getState() || {};
  const pluginToolbarsById = reduxState.undoGroup?.present?.pluginToolbarsById || {};
  const hasUnsavedChanges =
    open &&
    hasUndoSignatureChanged(
      getLastSavedSignature ? getLastSavedSignature() : null,
      getUndoSignature(reduxState.undoGroup || {})
    );

  // Narration preview (Task A/C): join scene-audios to the selected preflight
  // scene by navId. When a scene has more than one audio row (DB/legacy/
  // text-only reconciliation) prefer the one with narrationText, else the
  // first -- the same simplification ImageAnimatorModal.jsx already makes
  // for its narration preview; every real payload inspected has exactly one.
  const sceneAudioEntry =
    (sceneAudios?.scenes || []).find((s) => s.navId === selectedScene?.navId) || null;
  const narrationAudio =
    sceneAudioEntry?.audios?.find((a) => a.narrationText) || sceneAudioEntry?.audios?.[0] || null;
  const activeBoxId = playheadSec == null ? null : pickActiveCardBoxId(selectedImages, playheadSec);

  const allNotAnimated = scenes.flatMap((scene) =>
    (scene.images || [])
      .filter(isSelectableForAnimate)
      .map((img) => ({ ...img, navId: scene.navId, sceneIndex: scene.sceneIndex }))
  );
  const allAlreadyAnimatedCount = scenes.reduce(
    (sum, scene) => sum + (scene.images || []).filter((img) => img.alreadyAnimated).length,
    0
  );

  // Bir bulk-start için hem batch-wide opts'u hem de per-item override
  // üreticisini döndürür. AUTO: sunucu defaults + akıllı per-item süre/
  // pixverse escalation (itemAnimateOverrides). CUSTOM: kullanıcının seçtiği
  // provider/süre/döngü/çıktı batch-wide uygulanır; per-item override YOK
  // (kullanıcının açık süresi tüm görsellere geçsin, escalation ezmesin) —
  // ImageAnimatorModal'daki tek-görsel davranışıyla aynı.
  const buildStartConfig = () => {
    const defaults = preflight?.defaults || {};
    if (providerMode === "custom") {
      const provider = selectedProvider || defaults.provider;
      return {
        perItem: () => ({}),
        opts: {
          provider,
          durationSec: selectedDuration ?? undefined,
          loop: selectedLoop,
          outputs: [selectedOutput],
        },
      };
    }
    const provider = selectedProvider || defaults.provider;
    const supportedDurations = supportedDurationsForProvider(provider);
    return {
      perItem: (img) => itemAnimateOverrides(img.timing, supportedDurations, preflight?.providers),
      opts: {
        provider,
        durationSec: defaults.durationSec,
        loop: defaults.loop,
        outputs: defaults.outputs,
      },
    };
  };

  const handleAnimateScene = async () => {
    if (!selectedScene || !selectedNotAnimated.length) return;
    const { perItem, opts } = buildStartConfig();
    const items = selectedNotAnimated.map((img) => ({
      boxId: img.boxId,
      navId: selectedScene.navId,
      sceneIndex: selectedScene.sceneIndex,
      sourceUrl: img.sourceUrl,
      ...perItem(img),
    }));
    setConfirmScope(null);
    stopPreview();
    await start(items, opts);
  };

  const handleAnimateAll = async () => {
    if (!allNotAnimated.length) return;
    const { perItem, opts } = buildStartConfig();
    const items = allNotAnimated.map((img) => ({
      boxId: img.boxId,
      navId: img.navId,
      sceneIndex: img.sceneIndex,
      sourceUrl: img.sourceUrl,
      ...perItem(img),
    }));
    setConfirmScope(null);
    stopPreview();
    await start(items, opts);
  };

  // Re-fires the same window event 0/500/1100ms later, mirroring
  // EditImageToolUI.jsx's dispatchOpenImageAnimator. EditorShortcuts'
  // listener bails on its first fire to select the target box/nav (a Redux
  // dispatch that hasn't reached this component's props yet) -- a single
  // fire would only ever select and never actually open the popup when the
  // clicked card isn't already the canvas selection.
  const dispatchOpenImageAnimator = (detail) => {
    const fire = () => window.dispatchEvent(new CustomEvent("aidea:openImageAnimator", { detail }));
    fire();
    setTimeout(fire, 500);
    setTimeout(fire, 1100);
  };

  // Per-card primary action (Task 37 ASK 2/4): closes the panel and opens the
  // normal single-image Image Animator popup on this image instead of a
  // bulk-of-one start() -- that path isn't subject to the one-batch-per-
  // training lock (see ASK 3). `returnTo` lets the popup's Back button reopen
  // this panel on the same scene (see EditorShortcuts.jsx / ImageAnimatorModal.jsx).
  const handleOpenSingleAnimator = (img) => {
    if (!selectedScene) return;
    dispatchOpenImageAnimator({
      boxId: img.boxId,
      navId: selectedScene.navId,
      imageUrl: img.sourceUrl,
      operation: "animate",
      returnTo: { panel: "animator", navId: selectedScene.navId },
    });
    handleClose();
  };

  // Turn an already-animated box back into its original still image. Works
  // for both sources (the box-swap decision is resolved against live Redux
  // state inside the thunk, not the possibly-stale preflight snapshot a card
  // was rendered from) and is a normal undo-tracked edit.
  // cardBoxId keys the per-card UI state (spinner/error/confirm); targetBoxId is
  // the box the thunk actually reverts. They differ for a box-swap that has
  // already been applied: the card is still keyed by the source image's
  // original boxId (from preflight), but the live/reloaded box is the NEW
  // EnrichedPlayer id the swap minted (findAppliedAnimation resolves it). Fall
  // back to cardBoxId when there's no swap (url-update / not-yet-applied).
  const handleRevert = async (cardBoxId, targetBoxId) => {
    const boxId = targetBoxId || cardBoxId;
    setRevertConfirmBoxId(null);
    setRevertingBoxId(cardBoxId);
    setRevertError(null);
    const result = await dispatch(revertAnimatedBox(boxId));
    setRevertingBoxId(null);
    if (!result?.ok) {
      setRevertError({
        boxId: cardBoxId,
        message: i18n.t("AnimatorPanel.RevertNotFound", {
          defaultValue: "Original image not found — can't revert this one.",
        }),
      });
      return;
    }
    // Persist immediately — a revert only writes Redux, which resets to the
    // last saved state on a browser refresh; saving keeps the reverted state
    // (stopPreview + preflight reload happen in handleSaveAndRefresh's success).
    handleSaveAndRefresh();
  };

  // Approve a single "animated, not yet applied" card. Reuses the exact
  // dispatch the batch-wide Apply uses (applyBulkAnimationResults), just with
  // a one-item array -- same box-swap-or-url-update decision, same
  // undo-exclusion, no new write path. The Redux write is synchronous and
  // doesn't touch preflight/narration -- no load(), no stopPreview() needed.
  // jobId (from the live batch item when tracked, else preflight's own
  // per-image animationJobId once the panel is reopened) also marks the job
  // approved server-side, same as the tray's own Onayla -- best-effort,
  // never blocks the apply. Older servers omit animationJobId entirely, so
  // this is skipped for those.
  const handleApprove = (img, resultUrl, jobId) => {
    if (!selectedScene || !resultUrl) return;
    dispatch(
      applyBulkAnimationResults([{ boxId: img.boxId, navId: selectedScene.navId, resultUrl }])
    );
    if (jobId) approveJob(jobId);
    // Persist immediately so the "Applied" state survives a browser refresh —
    // applyBulkAnimationResults only writes Redux (synchronous, so store.getState()
    // inside handleSaveAndRefresh already sees it), which resets to the last saved
    // state on reload. Without this, an approved card snaps back to "not animated".
    handleSaveAndRefresh();
  };

  // Plain refresh (no unsaved changes to save first) -- re-fetches preflight
  // without going through handleSaveAndRefresh's save round-trip.
  const handleRefreshOnly = () => {
    stopPreview();
    load();
  };

  const togglePlay = () => {
    const el = audioRef.current;
    if (!el || !narrationAudio?.filePath) return;
    if (playing) {
      el.pause();
      setPlaying(false);
    } else {
      el.play();
      setPlaying(true);
    }
  };

  // scene-audios doesn't expose the per-entry startTime offset the Visor
  // uses (see scenePreviewSync.es6) -- 0 is correct as long as narrationAudio
  // is the scene's only/first entry, true for every payload inspected.
  const handleTimeUpdate = () => {
    const el = audioRef.current;
    // Only track the playhead while actually playing. Switching scenes swaps the
    // <audio src>, which resets currentTime to 0 and fires a stray timeupdate —
    // without this guard that lit up the FIRST card with the purple "playing now"
    // highlight even though nothing was playing (and it couldn't be clicked away,
    // since it isn't a selection). el.paused is the real media state, so it has
    // no React-state timing race on play start.
    if (!el || el.paused) return;
    setPlayheadSec(computeScenePlayhead(0, el.currentTime));
  };

  const handleEnded = () => setPlaying(false);

  // Sahne aksiyonu artık AppModal footer'ında (QuestionGeneratorModal'daki alt
  // "Oluştur" butonu gibi -- görsel tutarlılık): duruma göre confirmLabel/
  // onConfirm/cancelLabel üretir. confirmScope aktifken footer boş kalır; onay
  // uyarı şeridi (Start/Cancel) gövdede kendi butonlarıyla devam eder.
  const sceneFooterAction = (() => {
    if (!open || !hasImages || !selectedScene || confirmScope) return {};
    // Faz artık AKTİF SAHNEYE göre (scenePhase). Başka sahnede iş çalışsa bile
    // bu sahne idle ise canlandır butonu gelir — sahneler bağımsız.
    if (scenePhase === "running") {
      return {
        confirmLabel: i18n.t("AnimateScope.ProgressOf", {
          done: sceneDone,
          total: sceneLiveItems.length,
        }),
        confirmDisabled: true,
        cancelLabel: i18n.t("AnimateScope.CancelButton"),
        onCancel: () => cancelScene(activeNavId),
      };
    }
    if (scenePhase === "terminal") {
      return {
        confirmLabel: i18n.t("AnimatorPanel.ApplyToScene", {
          defaultValue: "Apply to this scene",
        }),
        confirmDisabled: applyingNavId === activeNavId,
        onConfirm: () => {
          stopPreview();
          applyScene(activeNavId);
        },
      };
    }
    // Seçili görsel yoksa footer butonu yok (animate edilecek bir şey yok).
    if (!selectedNotAnimated.length) return {};
    return {
      confirmLabel: i18n.t("AnimateScope.AnimateSceneButton", {
        count: selectedNotAnimated.length,
        defaultValue: "Animate all in this scene",
      }),
      onConfirm: () => setConfirmScope("scene"),
    };
  })();

  const renderTrainingAction = () => {
    if (confirmScope || anyPending || !allNotAnimated.length) return null;
    return (
      <button
        type="button"
        className="app-modal__btn app-modal__btn--primary animator-panel__animate-scene-btn"
        onClick={() => setConfirmScope("all")}
      >
        {i18n.t("AnimateScope.AnimateAllButton", {
          count: allNotAnimated.length,
          defaultValue: "Animate all ({{count}})",
        })}
      </button>
    );
  };

  // Refresh affordance -- artık footer'ın SOL alt köşesinde (üst toolbar'daki
  // geniş beyaz boşluğu kaldırmak için). Kaydedilmemiş değişiklik varsa kompakt
  // "Kaydet ve yenile", yoksa düz "Listeyi yenile".
  const refreshAffordance =
    !loading && !error ? (
      hasUnsavedChanges ? (
        <div className="animator-panel__save-inline">
          <button
            type="button"
            className="app-modal__btn app-modal__btn--ghost"
            onClick={handleSaveAndRefresh}
            disabled={saving}
            title={i18n.t("AnimatorPanel.UnsavedNotice", {
              defaultValue: "Unsaved changes aren't in this list yet — save to include them.",
            })}
          >
            {saving && (
              <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
            )}
            {i18n.t("AnimatorPanel.SaveAndRefresh", { defaultValue: "Save and refresh" })}
          </button>
          {saveError && (
            <span className="animator-panel__card-error animator-panel__save-inline-error">
              {i18n.t("AnimatorPanel.SaveError", {
                defaultValue:
                  "Couldn't save your changes. The list below is still your last saved version.",
              })}
            </span>
          )}
        </div>
      ) : (
        <button
          type="button"
          className="app-modal__btn app-modal__btn--ghost"
          onClick={handleRefreshOnly}
        >
          {i18n.t("AnimatorPanel.RefreshList", { defaultValue: "Refresh list" })}
        </button>
      )
    ) : null;

  // Alt footer: sol = refresh, sağ = sahne aksiyonu (animate/apply/cancel).
  // QuestionGeneratorModal'daki alt buton düzeniyle tutarlı. loading/error/
  // görselsiz durumda footer yok (boş bar gelmesin).
  const modalFooter =
    loading || error || !hasImages ? undefined : (
      <div className="animator-panel__footer">
        <div className="animator-panel__footer-actions">
          {refreshAffordance}
          {sceneFooterAction.cancelLabel && (
            <button
              type="button"
              className="app-modal__btn app-modal__btn--ghost"
              onClick={sceneFooterAction.onCancel}
            >
              {sceneFooterAction.cancelLabel}
            </button>
          )}
          {sceneFooterAction.confirmLabel && (
            <button
              type="button"
              className="app-modal__btn app-modal__btn--primary"
              onClick={sceneFooterAction.onConfirm}
              disabled={sceneFooterAction.confirmDisabled}
            >
              {sceneFooterAction.confirmLabel}
            </button>
          )}
          {false && renderTrainingAction()}
        </div>
      </div>
    );

  return (
    <>
      <AppModal
        show={open}
        onClose={handleClose}
        size="xl"
        className="animator-modal"
        title={i18n.t("Animator", { defaultValue: "Animator" })}
        footer={modalFooter}
      >
        <div className="animator-panel">
          <div className="animator-panel__toolbar">
            {providerOptions.length > 0 && (
              <>
                <div className="animator-panel__provider">
                  <span className="animator-panel__provider-label">
                    {i18n.t("FileModal.APIProviders.ImageStyles.AIProvider", {
                      defaultValue: "AI Provider",
                    })}
                  </span>
                  {/* Auto/Custom toggle + chip'ler aynı satırda (Avatar
                      .asm-av__model-toggle). Custom seçilince chip'ler toggle'ın
                      YANINDA belirir; Auto'da hiç render edilmez (alt boşluk yok). */}
                  <div className="animator-panel__provider-row">
                    <div
                      className="animator-panel__provider-toggle"
                      role="tablist"
                      aria-label={i18n.t("FileModal.APIProviders.ImageStyles.AIProvider", {
                        defaultValue: "AI Provider",
                      })}
                    >
                      <button
                        type="button"
                        role="tab"
                        aria-selected={providerMode === "auto"}
                        className={`animator-panel__provider-toggle-btn${
                          providerMode === "auto"
                            ? " animator-panel__provider-toggle-btn--active"
                            : ""
                        }`}
                        disabled={saving}
                        onClick={() => {
                          setProviderMode("auto");
                          setSelectedProvider(null);
                        }}
                      >
                        {i18n.t("FileModal.APIProviders.ImageStyles.AutoProvider", {
                          defaultValue: "Auto",
                        })}
                      </button>
                      <button
                        type="button"
                        role="tab"
                        aria-selected={providerMode === "custom"}
                        className={`animator-panel__provider-toggle-btn${
                          providerMode === "custom"
                            ? " animator-panel__provider-toggle-btn--active"
                            : ""
                        }`}
                        disabled={saving}
                        onClick={() => {
                          setProviderMode("custom");
                          setSelectedProvider(
                            (prev) =>
                              prev ||
                              preflight?.defaults?.provider ||
                              providerOptions[0]?.value ||
                              null
                          );
                        }}
                      >
                        {i18n.t("FileModal.APIProviders.ImageStyles.CustomProvider", {
                          defaultValue: "Custom",
                        })}
                      </button>
                    </div>
                    {/* Chip'ler hep görünür; Auto'da disabled (etkin sağlayıcı =
                      selectedProvider ?? sunucu default'u vurgulu), Özel'de
                      seçilebilir. */}
                    <div className="animator-panel__provider-chips">
                      {providerOptions.map((opt) => (
                        <button
                          key={opt.value}
                          type="button"
                          className={`animator-panel__provider-chip${
                            (selectedProvider || preflight?.defaults?.provider) === opt.value
                              ? " animator-panel__provider-chip--active"
                              : ""
                          }`}
                          disabled={saving || providerMode !== "custom"}
                          onClick={() => setSelectedProvider(opt.value)}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                {/* Süre/Döngü/Çıktı — provider bloğunun SAĞINDA, boş alana yayılır
                  (flex:1). HER ZAMAN görünür; Auto'da disabled (sabit,
                  değiştirilemez, sunucu default'unu gösterir), Özel'de aktif.
                  Hep DOM'da olduğu için mod değişiminde yükseklik sabit kalır,
                  içerik alta/üste kaymaz. */}
                <div className="animator-panel__custom-controls">
                  <div className="animator-panel__custom-control">
                    <span className="animator-panel__provider-label">
                      {i18n.t("Duration", { defaultValue: "Duration" })}
                    </span>
                    <div className="animator-panel__provider-chips">
                      <button
                        type="button"
                        className={`animator-panel__provider-chip${
                          selectedDuration === null ? " animator-panel__provider-chip--active" : ""
                        }`}
                        disabled={saving || providerMode !== "custom"}
                        onClick={() => setSelectedDuration(null)}
                      >
                        {i18n.t("Auto", { defaultValue: "Auto" })}
                      </button>
                      {activeSupportedDurations.map((sec) => (
                        <button
                          key={sec}
                          type="button"
                          className={`animator-panel__provider-chip${
                            selectedDuration === sec ? " animator-panel__provider-chip--active" : ""
                          }`}
                          disabled={saving || providerMode !== "custom"}
                          onClick={() => setSelectedDuration(sec)}
                        >
                          {i18n.t("DurationSecondsShort", { count: sec }) || `${sec}s`}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="animator-panel__custom-control">
                    <span className="animator-panel__provider-label">
                      {i18n.t("Loop", { defaultValue: "Loop" })}
                    </span>
                    <div className="animator-panel__provider-toggle" role="tablist">
                      {[
                        { value: true, label: i18n.t("Loop", { defaultValue: "Loop" }) },
                        {
                          value: false,
                          label: i18n.t("Play Once", { defaultValue: "Play Once" }),
                        },
                      ].map((opt) => (
                        <button
                          key={String(opt.value)}
                          type="button"
                          role="tab"
                          aria-selected={selectedLoop === opt.value}
                          className={`animator-panel__provider-toggle-btn${
                            selectedLoop === opt.value
                              ? " animator-panel__provider-toggle-btn--active"
                              : ""
                          }`}
                          disabled={saving || providerMode !== "custom"}
                          onClick={() => setSelectedLoop(opt.value)}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="animator-panel__custom-control">
                    <span className="animator-panel__provider-label">
                      {i18n.t("Output Format", { defaultValue: "Output Format" })}
                    </span>
                    <div className="animator-panel__provider-toggle" role="tablist">
                      {[
                        { fmt: "webp", label: "WebP" },
                        { fmt: "gif", label: "GIF" },
                      ].map((opt) => (
                        <button
                          key={opt.fmt}
                          type="button"
                          role="tab"
                          aria-selected={selectedOutput === opt.fmt}
                          className={`animator-panel__provider-toggle-btn${
                            selectedOutput === opt.fmt
                              ? " animator-panel__provider-toggle-btn--active"
                              : ""
                          }`}
                          disabled={saving || providerMode !== "custom"}
                          onClick={() => setSelectedOutput(opt.fmt)}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
          {confirmScope === "all" && (
            <div className="animator-panel__confirm-strip">
              <span>
                {i18n.t("AnimateScope.ConfirmDescCount", {
                  count: allNotAnimated.length,
                  alreadyAnimated: allAlreadyAnimatedCount,
                })}{" "}
                {i18n.t("AnimateScope.WarningNotRevertable")}{" "}
                {i18n.t("AnimateScope.WarningCancelCharges")}
              </span>
              <div className="animator-panel__confirm-strip-actions">
                <button
                  type="button"
                  className="app-modal__btn app-modal__btn--ghost"
                  onClick={() => setConfirmScope(null)}
                >
                  {i18n.t("Cancel")}
                </button>
                <button
                  type="button"
                  className="app-modal__btn app-modal__btn--primary"
                  onClick={handleAnimateAll}
                  disabled={starting}
                >
                  {starting && (
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    />
                  )}
                  {i18n.t("AnimateScope.StartButton")}
                </button>
              </div>
            </div>
          )}
          {loading ? (
            <div className="animator-panel__state">
              <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
              {i18n.t("Loading")}
            </div>
          ) : error ? (
            <div className="animator-panel__state">
              <p>{i18n.t("Error")}</p>
              <button
                type="button"
                className="app-modal__btn app-modal__btn--primary"
                onClick={() => load()}
              >
                {i18n.t("AnimatorPanel.Retry", { defaultValue: "Retry" })}
              </button>
            </div>
          ) : !hasImages ? (
            <div className="animator-panel__state">{i18n.t("AnimateScope.NoImages")}</div>
          ) : (
            <div className="animator-panel__layout">
              <div className="animator-panel__scene-list">
                <div className="animator-panel__scene-list-header">{i18n.t("Scenes")}</div>
                {scenes.map((scene, idx) => {
                  const images = scene.images || [];
                  // Sayaç PAYDASI: gösterilen tüm görseller (animate edilmiş +
                  // edilebilir). Böylece animate edilmişler de sayıya dahil.
                  const displayableCount = images.filter(
                    (img) => img.alreadyAnimated || img.animatable
                  ).length;
                  // PAY: yalnızca seçili-edilebilir. Animate edilmişler
                  // seçilemez → paya dahil değil.
                  const selectableImages = images.filter(isSelectableForAnimate);
                  const selectableCount = selectableImages.length;
                  // Bu sahnenin depolanan seçimi (Map). Girişi olmayan/hiç
                  // ziyaret edilmemiş sahnede varsayılan "hepsi seçili".
                  // Girişi varsa gerçek seçim sayısı gösterilir — pasif sahnede
                  // de doğru kalsın diye (aksi halde başka sahneye geçince rail
                  // yanlışça sıfırlanır).
                  const storedSelection = selectionByNav[scene.navId];
                  const selectedCount = storedSelection
                    ? selectableImages.filter((img) => storedSelection.has(img.boxId)).length
                    : selectableCount;
                  const isActive = selectedScene?.navId === scene.navId;
                  // Gerçek sahne numarası: preflight yalnız görsellü sahneleri
                  // döndürür, o yüzden dizideki sıra (idx+1) eğitimdeki gerçek
                  // sahne no'yu vermez (soru sahneleri atlanır). scene.sceneIndex
                  // sunucudan 1-tabanlı tam navItemsIds pozisyonu gelir; 0 (navId
                  // bulunamadı) durumunda idx+1'e düş.
                  const sceneNo = Number(scene.sceneIndex) || idx + 1;
                  return (
                    <button
                      key={scene.navId}
                      type="button"
                      className={`animator-panel__scene-item${isActive ? " animator-panel__scene-item--active" : ""}`}
                      onClick={() => selectScene(scene.navId)}
                      title={scene.title || `${i18n.t("Scene")} ${sceneNo}`}
                    >
                      <span className="animator-panel__scene-index">{sceneNo}</span>
                      <span className="animator-panel__scene-name">
                        {scene.title || `${i18n.t("Scene")} ${sceneNo}`}
                      </span>
                      {/* Bu sahnede hâlâ çalışan job varsa spinner (başka sahneye
                          geçince de görünür — devam ettiğini belli eder). Yoksa:
                          aktif sahne seçili/seçilebilir; diğerleri seçilebilir/
                          seçilebilir. */}
                      {runningNavIds.has(scene.navId) ? (
                        <span
                          className="animator-panel__scene-spinner"
                          title={i18n.t("AnimateScope.ItemRunning", { defaultValue: "Animating…" })}
                        >
                          <span
                            className="spinner-border spinner-border-sm"
                            role="status"
                            aria-hidden="true"
                          />
                        </span>
                      ) : (
                        <span className="animator-panel__scene-progress">
                          {`${selectedCount}/${displayableCount}`}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
              <div className="animator-panel__cards-panel">
                <div className="animator-panel__cards-header">
                  <span className="animator-panel__cards-title">
                    {selectedScene?.title ||
                      `${i18n.t("Scene")} ${
                        Number(selectedScene?.sceneIndex) || scenes.indexOf(selectedScene) + 1
                      }`}
                  </span>
                </div>
                <div className="animator-panel__narration">
                  <div className="animator-panel__narration-header">
                    <span className="animator-panel__narration-label">
                      {i18n.t("AnimatorPanel.NarrationLabel", { defaultValue: "Narration" })}
                    </span>
                    <button
                      type="button"
                      className="animator-panel__play-btn"
                      onClick={togglePlay}
                      disabled={!narrationAudio?.filePath}
                    >
                      {playing ? <LuPause size={13} /> : <LuPlay size={13} />}
                      {playing
                        ? i18n.t("AnimatorPanel.Pause", { defaultValue: "Pause" })
                        : i18n.t("AnimatorPanel.Play", { defaultValue: "Play" })}
                    </button>
                  </div>
                  <div className="animator-panel__narration-text">
                    {narrationAudio?.narrationText || (
                      <span className="animator-panel__narration-empty">
                        {i18n.t("AnimatorPanel.NoNarration", {
                          defaultValue: "No narration for this scene",
                        })}
                      </span>
                    )}
                  </div>
                  <audio
                    ref={audioRef}
                    src={narrationAudio?.filePath || undefined}
                    onTimeUpdate={handleTimeUpdate}
                    onEnded={handleEnded}
                    style={{ display: "none" }}
                  />
                </div>
                {confirmScope === "scene" && (
                  <div className="animator-panel__confirm-strip">
                    <span>
                      {i18n.t("AnimateScope.ConfirmSceneDescCount", {
                        count: selectedNotAnimated.length,
                        defaultValue:
                          "This will animate {{count}} infographic images in this scene.",
                      })}{" "}
                      {i18n.t("AnimateScope.WarningNotRevertable")}{" "}
                      {i18n.t("AnimateScope.WarningCancelCharges")}
                    </span>
                    <div className="animator-panel__confirm-strip-actions">
                      <button
                        type="button"
                        className="app-modal__btn app-modal__btn--ghost"
                        onClick={() => setConfirmScope(null)}
                      >
                        {i18n.t("Cancel")}
                      </button>
                      <button
                        type="button"
                        className="app-modal__btn app-modal__btn--primary"
                        onClick={handleAnimateScene}
                        disabled={starting}
                      >
                        {starting && (
                          <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                            aria-hidden="true"
                          />
                        )}
                        {i18n.t("AnimateScope.StartButton")}
                      </button>
                    </div>
                  </div>
                )}
                {bulkError && (
                  <div className="animator-panel__inline-alert animator-panel__inline-alert--error">
                    {bulkError}
                  </div>
                )}
                {/* Seçili / TOPLAM gösterilen (animate edilmiş dahil) — görsel
                    listesinin sağ üstünde. Pay = seçili-edilebilir; animate
                    edilmişler payda dahil ama paya değil. Seçim değiştikçe
                    dinamik. */}
                {displayableImages.length > 0 && (
                  <div className="animator-panel__cards-listbar">
                    <span className="animator-panel__count-badge">
                      {`${selectedNotAnimated.length}/${displayableImages.length}`}
                    </span>
                  </div>
                )}
                {displayableImages.length === 0 ? (
                  <div className="animator-panel__cards-empty">
                    {i18n.t("AnimatorPanel.NoAnimatableImages", {
                      defaultValue: "No images to animate in this scene",
                    })}
                  </div>
                ) : (
                  <div className="animator-panel__cards">
                    {displayableImages.map((img) => {
                      const live = liveByBoxId[img.boxId];
                      // Applied animation read straight from live/reloaded Redux
                      // (box-swap → EnrichedPlayer stashing this source url). This
                      // is what makes an already-applied card render as "Applied"
                      // after a refresh even when preflight's server-side
                      // alreadyAnimated match misses -- the saved canvas is the
                      // source of truth, not the API. url-update applies fall
                      // through to null (see findAppliedAnimation).
                      const appliedInRedux = findAppliedAnimation({
                        sourceUrl: img.sourceUrl,
                        pluginToolbarsById,
                      });
                      // img.resultUrl (preflight's "most recent library result",
                      // independent of any live batch) is the fix for "already-
                      // animated cards show only a thumbnail" -- the old code
                      // only ever displayed live.resultUrl, so a card reopened
                      // with no active batch fell straight back to the still.
                      const bestResultUrl =
                        live?.resultUrl || img.resultUrl || appliedInRedux?.url || null;
                      const displayUrl = bestResultUrl || img.sourceUrl;
                      const displayIsVideo = isVideoUrl(displayUrl);
                      const { isFailed, isDone, isRunning } = deriveCardLiveState(live);
                      // ...|| appliedInRedux: a box applied in Redux is animated
                      // regardless of what preflight reports, so the badge/actions
                      // survive a refresh.
                      const isAnimated = isDone || img.alreadyAnimated || !!appliedInRedux;
                      const applied =
                        isAnimated &&
                        isResultApplied({
                          boxId: img.boxId,
                          sourceUrl: img.sourceUrl,
                          resultUrl: bestResultUrl,
                          pluginToolbarsById,
                        });
                      const isUnsupported = !isAnimated && !isSelectableForAnimate(img);
                      // Predicted duration (Task 42 Part 2.4, escalation added
                      // Task 46): only meaningful before a card is animated --
                      // once it's animated the actual clip is what's on screen,
                      // this would just be a stale guess. Same
                      // itemAnimateOverrides the bulk-start payloads use, so a
                      // >15s card's label/tooltip matches what actually gets sent.
                      const predictedOverride =
                        !isAnimated && isSelectableForAnimate(img)
                          ? itemAnimateOverrides(
                              img.timing,
                              activeSupportedDurations,
                              preflight?.providers
                            )
                          : {};
                      const predictedDurationSec = predictedOverride.durationSec;
                      const isPixverseEscalated = predictedOverride.provider === "pixverse";
                      const durationSuffix =
                        predictedDurationSec !== undefined
                          ? ` · ${i18n.t("AnimatorPanel.PredictedDuration", { sec: predictedDurationSec, defaultValue: "{{sec}}s animation" })}`
                          : "";
                      const unsupportedReason = i18n.t("AnimatorPanel.UnsupportedFormatReason", {
                        defaultValue: "Unsupported format",
                      });
                      // Task 37 ASK 2/3: available in every card state (incl.
                      // already-animated) and stays up while a bulk batch runs
                      // elsewhere -- only hidden for THIS card's own running item.
                      const animateAction = deriveCardAnimateAction({
                        isAnimated,
                        isRunning,
                        animatable: !!img.animatable,
                      });
                      // Bulk seçim: yalnızca aktif sahne idle iken, animate
                      // edilebilir ve henüz animate edilmemiş kartlar seçilebilir.
                      // Başka sahnede iş çalışsa bile bu sahne idle ise eleme
                      // açık. Seçili değilse kart soluk (bulk-start atlar).
                      const isSelectable =
                        scenePhase === "idle" && !isAnimated && isSelectableForAnimate(img);
                      const isSelected = selectedBoxIds.has(img.boxId);
                      const isDeselected = isSelectable && !isSelected;
                      return (
                        <div
                          key={img.boxId}
                          className={`animator-panel__card${isUnsupported ? " animator-panel__card--unsupported" : ""}${img.boxId === activeBoxId ? " animator-panel__card--preview-active" : ""}${isDeselected ? " animator-panel__card--deselected" : ""}`}
                        >
                          {/* Seçilebilir kartlarda thumb'a tıklamak seçimi aç/kapatır
                            (checkbox yok — seçili değilse kart soluklaşır). */}
                          <div
                            className={`animator-panel__card-thumb${isSelectable ? " animator-panel__card-thumb--selectable" : ""}`}
                            onClick={isSelectable ? () => toggleSelect(img.boxId) : undefined}
                            role={isSelectable ? "button" : undefined}
                            title={
                              isSelectable
                                ? i18n.t("AnimatorPanel.SelectForAnimate", {
                                    defaultValue: "Select for animation",
                                  })
                                : undefined
                            }
                          >
                            {displayIsVideo ? (
                              <video
                                src={displayUrl}
                                muted
                                autoPlay
                                loop
                                playsInline
                                poster={live?.thumbnailUrl || img.thumbnailUrl}
                              />
                            ) : (
                              <img src={displayUrl} alt={img.fileName || ""} />
                            )}
                            {isRunning && (
                              <span className="animator-panel__card-spinner">
                                <span
                                  className="spinner-border spinner-border-sm"
                                  role="status"
                                  aria-hidden="true"
                                />
                              </span>
                            )}
                            <span
                              className={`animator-panel__card-badge${
                                isRunning
                                  ? " animator-panel__card-badge--running"
                                  : isFailed
                                    ? " animator-panel__card-badge--failed"
                                    : isAnimated
                                      ? " animator-panel__card-badge--animated"
                                      : isUnsupported
                                        ? " animator-panel__card-badge--unsupported"
                                        : ""
                              }`}
                            >
                              {isRunning
                                ? i18n.t("AnimateScope.ItemRunning", { defaultValue: "Animating…" })
                                : isFailed
                                  ? i18n.t("AnimateScope.ItemFailed")
                                  : isAnimated
                                    ? applied
                                      ? i18n.t("AnimatorPanel.AppliedBadge", {
                                          defaultValue: "Applied",
                                        })
                                      : i18n.t("AnimateScope.AlreadyAnimatedBadge")
                                    : isUnsupported
                                      ? unsupportedReason
                                      : i18n.t("AnimatorPanel.NotAnimated", {
                                          defaultValue: "Not animated",
                                        })}
                            </span>
                          </div>
                          <div className="animator-panel__card-info">
                            {revertConfirmBoxId === img.boxId ? (
                              <div className="animator-panel__card-revert-confirm">
                                <span>
                                  {i18n.t("AnimatorPanel.RevertConfirm", {
                                    defaultValue: "Revert to the original image?",
                                  })}
                                </span>
                                <div className="animator-panel__card-revert-confirm-actions">
                                  <button
                                    type="button"
                                    className="app-modal__btn app-modal__btn--ghost"
                                    onClick={() => setRevertConfirmBoxId(null)}
                                  >
                                    {i18n.t("Cancel")}
                                  </button>
                                  <button
                                    type="button"
                                    className="app-modal__btn app-modal__btn--primary"
                                    onClick={() => handleRevert(img.boxId, appliedInRedux?.boxId)}
                                  >
                                    {i18n.t("AnimatorPanel.RevertButton", {
                                      defaultValue: "Revert",
                                    })}
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <>
                                <p className="animator-panel__card-name" title={img.fileName}>
                                  {img.fileName}
                                </p>
                                <p
                                  className="animator-panel__card-range"
                                  title={isPixverseEscalated ? "Pixverse" : undefined}
                                >
                                  {formatTimeRange(img.timing)}
                                  {durationSuffix}
                                </p>
                                {revertError?.boxId === img.boxId && (
                                  <p className="animator-panel__card-error">
                                    {revertError.message}
                                  </p>
                                )}
                                {/* Failed item: only an AI-provider refusal (safety /
                                  copyright) gets a reason line -- other failures keep
                                  the bare "failed" badge, the raw text isn't user copy. */}
                                {isFailed && mapAiProviderErrorMessage(live?.error) && (
                                  <p className="animator-panel__card-error">
                                    {mapAiProviderErrorMessage(live?.error)}
                                  </p>
                                )}
                                <div className="animator-panel__card-actions">
                                  {displayUrl && (
                                    <button
                                      type="button"
                                      className="app-modal__btn app-modal__btn--ghost"
                                      onClick={() => setLightboxUrl(displayUrl)}
                                    >
                                      {i18n.t("AnimateScope.PreviewButton")}
                                    </button>
                                  )}
                                  {animateAction.show && (
                                    <button
                                      type="button"
                                      className="app-modal__btn app-modal__btn--primary"
                                      onClick={() => handleOpenSingleAnimator(img)}
                                      disabled={animateAction.disabled}
                                      title={animateAction.disabled ? unsupportedReason : undefined}
                                    >
                                      {animateAction.reanimate
                                        ? i18n.t("AnimatorPanel.ReanimateButton", {
                                            defaultValue: "Re-animate",
                                          })
                                        : i18n.t("AnimatorPanel.AnimateButton", {
                                            defaultValue: "Animate",
                                          })}
                                    </button>
                                  )}
                                  {isAnimated && !applied && !isRunning && (
                                    <button
                                      type="button"
                                      className="app-modal__btn app-modal__btn--primary"
                                      onClick={() =>
                                        handleApprove(
                                          img,
                                          bestResultUrl,
                                          live?.jobId ?? img.animationJobId
                                        )
                                      }
                                      disabled={!bestResultUrl}
                                    >
                                      {i18n.t("Approve")}
                                    </button>
                                  )}
                                  {applied && !isRunning && (
                                    <button
                                      type="button"
                                      className="app-modal__btn app-modal__btn--ghost"
                                      onClick={() => setRevertConfirmBoxId(img.boxId)}
                                      disabled={revertingBoxId === img.boxId}
                                    >
                                      {revertingBoxId === img.boxId && (
                                        <span
                                          className="spinner-border spinner-border-sm"
                                          role="status"
                                          aria-hidden="true"
                                        />
                                      )}
                                      {i18n.t("AnimatorPanel.RevertButton", {
                                        defaultValue: "Revert",
                                      })}
                                    </button>
                                  )}
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </AppModal>
      <Lightbox url={lightboxUrl} onClose={() => setLightboxUrl(null)} />
    </>
  );
}
